import { CreatorAction, CreatorActionType, UUID } from '@/types/types';
import { CreatorState } from '@/types/creator';
import { AttributeValue } from '@/api/AttributeValueApi';
import { WritableDraft } from '@reduxjs/toolkit';
import { clearInvalidWingDivisions } from '@/utils/creatorUtils';
import APP_CONFIG from '@/configs/appConfig';

class ActionHandler {
	constructor() {}

	handleActions(
		state: WritableDraft<CreatorState>,
		attributeValue: AttributeValue
	) {
		if (!attributeValue.actions) return;

		const withMosquitoNet = attributeValue.actions.some(
			(action) =>
				action.type === CreatorActionType.WITH_MOSQUITO_NET &&
				action.payload.withMosquitoNet
		);

		for (const action of attributeValue.actions) {
			this.handleAction(state, action, attributeValue.id, withMosquitoNet);
		}
	}

	handleDeselect(
		state: WritableDraft<CreatorState>,
		attributeValue: AttributeValue
	) {
		if (!attributeValue.actions) return;
		for (const action of attributeValue.actions) {
			this.handleRemoveConstraint(state, action, attributeValue.id);
		}
	}

	private handleRemoveConstraint(
		state: WritableDraft<CreatorState>,
		action: CreatorAction,
		attributeValueId: UUID
	) {
		delete state.configurationConfig.constraints[action.type][attributeValueId];

		switch (action.type) {
			case CreatorActionType.SET_SEGMETN_MIN_WIDTH:
				this.calculateMinWidthConstraint(state);
				break;
			case CreatorActionType.SET_SEGMENT_MAX_WIDTH:
				this.calculateMaxWidthConstraint(state);
				break;
			case CreatorActionType.WITH_MOSQUITO_NET:
				this.setWithMosquitoNet(state);
				break;

			default:
				break;
		}
	}

	private handleAction(
		state: WritableDraft<CreatorState>,
		action: CreatorAction,
		attributeValueId: UUID,
		withMosquitoNet: boolean
	) {
		switch (action.type) {
			case CreatorActionType.SET_SEGMETN_MIN_WIDTH:
				state.configurationConfig.constraints[action.type][attributeValueId] = {
					value: action.payload.minWidth!,
					withMosquitoNet,
				};
				this.calculateMinWidthConstraint(state);
				break;
			case CreatorActionType.SET_SEGMENT_MAX_WIDTH:
				state.configurationConfig.constraints[action.type][attributeValueId] =
					action.payload.maxWidth!;
				this.calculateMaxWidthConstraint(state);
				break;
			case CreatorActionType.WITH_MOSQUITO_NET:
				state.configurationConfig.constraints[action.type][attributeValueId] =
					action.payload.withMosquitoNet!;
				this.setWithMosquitoNet(state);
				break;
			case CreatorActionType.WITH_ARMOR:
				state.configurationConfig.constraints[action.type][attributeValueId] = {
					withArmor: action.payload.withArmor!,
					maxShellsQuantityFormula: action.payload.maxShellsQuantityFormula!,
				};
				state.configurationConfig.project.maxShellsQuantityFormula =
					action.payload.maxShellsQuantityFormula!;
				this.setWithArmor(state);
				break;

			default:
				break;
		}
	}

	private calculateMinWidthConstraint(state: WritableDraft<CreatorState>) {
		const constraints = Object.values(
				state.configurationConfig.constraints[
					CreatorActionType.SET_SEGMETN_MIN_WIDTH
				]
			) as {
				value: number;
				withMosquitoNet: boolean;
			}[],
			noMosquitoNetConstraints = constraints.filter(
				(constraint) => !constraint.withMosquitoNet
			);

		const minWidthWithoutNet = Math.max(
				...noMosquitoNetConstraints.map((constraint) => constraint.value),
				APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH
			),
			minWidthWithNet = Math.max(
				...constraints.map((constraint) => constraint.value),
				APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH
			);

		state.configurationConfig.segment.recommendedMinWidthRules[0].value =
			minWidthWithoutNet;
		state.configurationConfig.segment.recommendedMinWidthRules[1].value =
			minWidthWithNet;

		this.setRecommendedMinWidth(state);
	}

	private calculateMaxWidthConstraint(state: WritableDraft<CreatorState>) {
		const maxWidth = Math.min(
			...(Object.values(
				state.configurationConfig.constraints[
					CreatorActionType.SET_SEGMENT_MAX_WIDTH
				]
			) as number[]),
			APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MAX_WIDTH
		);

		state.configurationConfig.segment.recommendedMaxWidth = maxWidth;
	}

	private setWithMosquitoNet(state: WritableDraft<CreatorState>) {
		const hasMosquitoNetConstraints =
			Object.keys(
				state.configurationConfig.constraints[
					CreatorActionType.WITH_MOSQUITO_NET
				]
			).length > 0;

		const withMosquitoNet =
			hasMosquitoNetConstraints &&
			Object.values(
				state.configurationConfig.constraints[
					CreatorActionType.WITH_MOSQUITO_NET
				]
			).every(Boolean);

		state.configurationConfig.project.allowMosquitoNet = withMosquitoNet;
		this.setRecommendedMinWidth(state);

		if (!withMosquitoNet)
			for (const segment of state.segments) {
				for (const wing of segment.window.wings) {
					wing.withMosquitoNet = false;
				}
				clearInvalidWingDivisions(segment.window);
			}
	}

	private setRecommendedMinWidth(state: WritableDraft<CreatorState>) {
		const ruleIndex = state.configurationConfig.project.allowMosquitoNet
			? 1
			: 0;

		state.configurationConfig.segment.recommendedMinWidth =
			state.configurationConfig.segment.recommendedMinWidthRules[
				ruleIndex
			].value;
	}

	private setWithArmor(state: WritableDraft<CreatorState>) {
		const hasArmorConstraints =
			Object.keys(
				state.configurationConfig.constraints[CreatorActionType.WITH_ARMOR]
			).length > 0;

		const withArmor =
			hasArmorConstraints &&
			Object.values(
				state.configurationConfig.constraints[CreatorActionType.WITH_ARMOR]
			)
				.map((constraint) => constraint as { withArmor: boolean })
				.map((constraint) => constraint.withArmor)
				.every(Boolean);

		state.configurationConfig.project.withArmor = withArmor;
	}
}

export default new ActionHandler();
